import { Card, CardHeader, CardPreview, Image, makeStyles, mergeClasses, SkeletonItem, Text, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import caseStudyService from "../../services/CaseStudyService";
import { Link } from "react-router-dom";
import Status from "../../enums/Status";
import { mergeStyles } from "@fluentui/merge-styles";
import { dolphinTokens } from "../../utils/Theme";
import ICaseStudyPreview from "../../models/caseStudy/ICaseStudyPreview";

const useStyles = makeStyles({
    contentWrapper: {
        paddingBottom: "3rem"
    },
    row: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "3rem"
    },
    sectionHeader: {
        textAlign: "center"
    },
    blueBadge: {
        backgroundColor: dolphinTokens.colorLightBlue,
        color: "white",
        padding: "0.5rem 1.5rem",
        borderRadius: "50px",
        fontSize: "0.875rem",
        textTransform: "uppercase",
        display: "inline-block"
    },
    cardCol: {
        maxWidth: "350px",
        width: "100%"
    },
    card: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        transition: "all 0.3s",

        "&:hover": {
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            transform: "translateY(-4px)"
        }
    },
    flexContainer: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "2rem"
    },
    flexLink: {
        flex: "1 1 350px",
        maxWidth: "350px",
        textDecoration: "none"
    },
    image: {
        width: "100%",
        height: "auto",
        maxHeight: "224px",
        objectFit: "cover",
        objectPosition: "center",
    },
    logo: {
        height: "32px",
        width: "auto",
        marginBottom: "1rem",
        backgroundColor: "rgba(255, 255, 255, 0.5)",
    },
    skeletons: {
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,

        "& > [role='progressbar']": {
            width: '100%'
        }
    }
});

const Placeholder = () => {
    const styles = useStyles();
    const cardClassName = mergeClasses(styles.card, styles.skeletons);

    const firstSkeletonClassName = mergeStyles({ height: '224px !important' });

    return (
        <div className={styles.cardCol}>
            <Card className={cardClassName}>
                <SkeletonItem shape="rectangle" className={firstSkeletonClassName} />
                <SkeletonItem size={36} shape="rectangle" />
                <SkeletonItem size={20} shape="rectangle" />
            </Card>
        </div>
    );
};

const CaseStudy = (props: { caseStudy: ICaseStudyPreview }) => {
    const { caseStudy } = props;
    const styles = useStyles();

    return (
        <Link className={styles.flexLink} to={`/casestudy/${caseStudy.slug}`}>
            <Card className={styles.card}>
                <CardPreview logo={<Image src={caseStudy.client.logo.url} alt={caseStudy.client.logo.altText} className={styles.logo} fetchPriority="low" loading="lazy" />}>
                    <Image src={caseStudy.thumbnail.url} alt={caseStudy.thumbnail.altText} className={styles.image} fetchPriority="low" loading="lazy" />
                </CardPreview>
                <CardHeader
                    header={caseStudy.client.name}
                    description={caseStudy.subtitle}
                    />
                <Text>
                    {caseStudy.client.industry || 'N/A'}
                </Text>
            </Card>
        </Link>
    );
};

export default function Portfolio() {
    const { t } = useTranslation("home");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [caseStudies, setCaseStudies] = useState<ICaseStudyPreview[]>([]);

    useEffect(() => {
        getPortfolio(setCaseStudies, setStatus);
    }, []);

    if (status === Status.LOAD_ERROR) {
        return null!
    }

    return (
        <div className={styles.contentWrapper}>
            <div className={styles.sectionHeader}>
                <Text as="span" className={styles.blueBadge}>
                    {t("portfolio.pill")}
                </Text>
            </div>
            <Text as="h2" size={700} align="center" block>
                {t("portfolio.title")}
            </Text>
            <Text as="h5" size={500} align="center" block>
                {t("portfolio.subtitle")}
            </Text>
            <div className={styles.flexContainer}>
                {
                    status === Status.LOADING &&
                    Array.from({ length: 3 }, (_, index) => <Placeholder key={index} />)
                }
                
                {
                    status === Status.LOAD_SUCCESS &&
                    caseStudies
                        .slice(0, 3)
                        .map((caseStudy) => <CaseStudy key={caseStudy.id} caseStudy={caseStudy} />)
                }
            </div>
        </div>
    );
}

async function getPortfolio(
    setCaseStudies: (caseStudies: ICaseStudyPreview[]) => void,
    setStatus: (status: Status) => void
) {
    try {
        const response = await caseStudyService.getCaseStudyPreviews();

        setCaseStudies(response);
        setStatus(Status.LOAD_SUCCESS);
    } catch (_) {
        setStatus(Status.LOAD_ERROR);
    }
}